import { useAuthStore } from '@/stores/auth-store';

/**
 * Default agent download link — a Google Drive direct-download URL
 * (uc?export=download). Overridable per-env via VITE_OPTIEDGE_AGENT_DOWNLOAD_URL.
 * The downloaded filename is whatever the Drive file is named — keep the Drive
 * file named `optiedge-agent-latest.pkg` and update it via
 * "Manage versions → Upload new version" so this ID/link stays stable.
 */
const DEFAULT_AGENT_DOWNLOAD_URL =
  'https://drive.google.com/uc?export=download&id=1dqvQLsUiOT8W2CubnyPaVVGz2Hul7-qw';

/**
 * EnrollLaptopPage — "My Account" → "Enroll my laptop"
 *
 * Staff-facing landing page for the optiedge-provisioner agent. Shows the
 * currently-signed-in staff member their subnet/role mapping, provides the
 * agent download link, and walks through the three-step CLI flow.
 *
 * v0.1.0-staff-alpha scope: this is a static instructions page. Future
 * enhancements (copy-enrollment-URL button, paste-this-token auto-flow for
 * the agent's webui, installer download with pre-signed identity) land when
 * PKCE + systray + webui ship in the agent.
 */

export function EnrollLaptopPage() {
  const adminUser = useAuthStore((s) => s.adminUser);

  const role = adminUser?.role ?? '';
  const email = adminUser?.email ?? '';
  const subnet = roleToSubnet(role);
  const downloadUrl =
    import.meta.env.VITE_OPTIEDGE_AGENT_DOWNLOAD_URL ?? DEFAULT_AGENT_DOWNLOAD_URL;

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-8">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold text-foreground">{'Enroll my laptop'}</h1>
        <p className="text-sm text-muted-foreground">
          {
            'Install the OptiEdge agent on your laptop to join the OptiSigns corporate VPN. Once connected, you can SSH into customer devices to troubleshoot.'
          }
        </p>
      </header>

      <section className="rounded-lg border bg-muted/30 p-6" data-testid="enroll-identity">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {'Signed in as'}
        </h2>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <dt className="text-muted-foreground">{'Email'}</dt>
          <dd className="font-mono text-foreground">{email || '—'}</dd>
          <dt className="text-muted-foreground">{'Role'}</dt>
          <dd className="font-mono text-foreground">{role || '—'}</dd>
          <dt className="text-muted-foreground">{'Will join subnet'}</dt>
          <dd className="font-mono text-foreground">{subnet}</dd>
        </dl>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">{'Setup (3 steps)'}</h2>

        <ol className="space-y-5">
          <Step
            n={1}
            title={'Download the agent'}
            body={
              <>
                <p className="text-sm text-muted-foreground">
                  {'Signed macOS installer (.pkg) — latest production build.'}
                </p>
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  data-testid="download-agent"
                >
                  {'Download optiedge (macOS)'}
                </a>
              </>
            }
          />

          <Step
            n={2}
            title={'Install + sign in'}
            body={
              <p className="text-sm text-muted-foreground">
                {
                  'Double-click the .pkg and follow the installer (enter your Mac password when prompted). When it finishes, the OptiEdge icon appears in your menu bar and a browser tab opens automatically — sign in with your @optisigns.com Google account to join the VPN.'
                }
              </p>
            }
          />

          <Step
            n={3}
            title={'Verify the tunnel'}
            body={
              <p className="text-sm text-muted-foreground">
                {
                  'When the menu-bar icon shows ● Connected, you\'re on the VPN and can SSH into customer devices. Any personal WG tunnel stays untouched on a separate utun interface.'
                }
              </p>
            }
          />
        </ol>
      </section>

      <footer className="rounded-lg border bg-muted/30 p-4 text-xs text-muted-foreground">
        <p>
          <strong>{'How this maps to the VPN:'}</strong>{' '}
          {
            'Your peer record is created in the sandbox (100.127.0.0/16) or prod (100.64.0.0/10) overlay depending on which build you installed. Your email and role are attached to the peer — the staff console shows them in the Network → Peers view.'
          }
        </p>
      </footer>
    </div>
  );
}

interface StepProps {
  n: number;
  title: string;
  body: React.ReactNode;
}

function Step({ n, title, body }: StepProps) {
  return (
    <li className="flex gap-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
        {n}
      </div>
      <div className="flex-1 space-y-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {body}
      </div>
    </li>
  );
}

/**
 * roleToSubnet — client-side display copy of the server-side map in
 * staff-enroll.service.ts. Shown here so staff know which subnet they'll
 * land on BEFORE they run the agent. Must stay in sync with the server map:
 *   - root, engineer → engineers
 *   - support, readonly → support
 */
function roleToSubnet(role: string): string {
  switch (role) {
    case 'root':
    case 'engineer':
      return 'engineers';
    case 'support':
    case 'readonly':
      return 'support';
    default:
      return '—';
  }
}
